import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Rss, RefreshCw, Loader2, Clock, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "business", "politics", "technology", "entertainment", "sports",
  "health", "science", "environment", "food", "tourism"
];

const languages = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
];

const countries = [
  { value: "us", label: "United States" },
  { value: "gb", label: "United Kingdom" },
  { value: "de", label: "Germany" },
  { value: "fr", label: "France" },
  { value: "in", label: "India" },
];

const AdminFeedConfig = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject, projects } = useProject();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fetchingId, setFetchingId] = useState(null);
  const [formData, setFormData] = useState({
    project_id: "",
    categories: [],
    keywords: "",
    language: "en",
    country: "us",
    is_active: true,
    auto_publish: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const params = currentProject ? `?project_id=${currentProject.id}` : "";
      const response = await axios.get(`${API}/feeds${params}`, { headers: getAuthHeaders() });
      setFeeds(response.data);
    } catch (error) {
      console.error("Failed to fetch feeds:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        project_id: formData.project_id || currentProject?.id,
        keywords: formData.keywords.split(",").map(k => k.trim()).filter(Boolean)
      };
      await axios.post(`${API}/feeds`, payload, { headers: getAuthHeaders() });
      toast.success("Feed configuration created");
      setDialogOpen(false);
      setFormData({
        project_id: "",
        categories: [],
        keywords: "",
        language: "en",
        country: "us",
        is_active: true,
        auto_publish: true,
      });
      await fetchFeeds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create feed config");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFetchNews = async (configId) => {
    setFetchingId(configId);
    try {
      const response = await axios.post(`${API}/feeds/${configId}/fetch`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      await fetchFeeds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch news");
    } finally {
      setFetchingId(null);
    }
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  return (
    <AdminLayout title="API Feed Configuration">
      <div className="space-y-6" data-testid="admin-feeds">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#64748B]">Configure external news API feeds for each project</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-feed-button">
                <Plus className="h-4 w-4 mr-2" />
                New Feed Config
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Feed Configuration</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select
                    value={formData.project_id || currentProject?.id || ""}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  >
                    <SelectTrigger data-testid="feed-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Badge
                        key={cat}
                        variant={formData.categories.includes(cat) ? "default" : "outline"}
                        className="cursor-pointer capitalize"
                        onClick={() => toggleCategory(cat)}
                        data-testid={`category-toggle-${cat}`}
                      >
                        {cat}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData({ ...formData, language: value })}
                    >
                      <SelectTrigger data-testid="feed-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                    >
                      <SelectTrigger data-testid="feed-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                  <Input
                    id="keywords"
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="economy, stocks, market"
                    data-testid="feed-keywords-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_active">Active</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="feed-active-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto_publish">Auto-publish fetched articles</Label>
                    <p className="text-xs text-[#64748B]">If off, fetched articles go to Reviews for manual approval.</p>
                  </div>
                  <Switch
                    id="auto_publish"
                    checked={formData.auto_publish}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_publish: checked })}
                    data-testid="feed-auto-publish-switch"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || formData.categories.length === 0} data-testid="save-feed-button">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">NewsAPI.org Integration</p>
                <p className="text-sm text-blue-700">
                  Configure categories and filters per project. Native categories (<i>business, entertainment, health, science, sports, technology</i>) use <code>/v2/top-headlines</code> with your country. Non-native (<i>politics, environment, food, tourism</i>) fall back to <code>/v2/everything</code> with keyword search. With <b>Auto-publish</b> on (default), fetched articles go live immediately on the public site. Set your NewsAPI key on Railway as <code>NEWS_API_KEY</code>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feeds List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 shadow-sm animate-pulse">
                <CardContent className="p-6 h-24" />
              </Card>
            ))}
          </div>
        ) : feeds.length > 0 ? (
          <div className="space-y-4">
            {feeds.map((feed) => {
              const project = projects.find(p => p.id === feed.project_id);
              return (
                <Card key={feed.id} className="border-0 shadow-sm" data-testid={`feed-card-${feed.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Rss className={`h-5 w-5 ${feed.is_active ? "text-green-600" : "text-gray-400"}`} />
                          <h3 className="font-semibold text-[#0F172A]">{project?.name || "Unknown Project"}</h3>
                          <Badge variant={feed.is_active ? "default" : "secondary"}>
                            {feed.is_active ? "Active" : "Inactive"}
                          </Badge>
                          <Badge variant={feed.auto_publish ? "default" : "outline"} className={feed.auto_publish ? "bg-green-600" : ""}>
                            {feed.auto_publish ? "Auto-publish" : "Manual review"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          {feed.categories.map((cat) => (
                            <Badge key={cat} variant="outline" className="text-xs capitalize">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#64748B]">
                          <span>{feed.language.toUpperCase()}</span>
                          <span>•</span>
                          <span>{feed.country.toUpperCase()}</span>
                          {feed.last_fetch && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Last fetch: {new Date(feed.last_fetch).toLocaleString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleFetchNews(feed.id)}
                        disabled={fetchingId === feed.id}
                        data-testid={`fetch-news-${feed.id}`}
                      >
                        {fetchingId === feed.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Fetch News
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Rss className="h-12 w-12 mx-auto mb-4 text-[#64748B] opacity-50" />
              <h3 className="font-semibold text-[#0F172A] mb-2">No feed configurations</h3>
              <p className="text-[#64748B] mb-4">Create a feed config to start importing news</p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Feed Config
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFeedConfig;
